import React, { useEffect, useRef, useState } from 'react';
import whiteboardService, { drawStrokes, NOTE_COLOR } from '../services/whiteboardService';
import './Whiteboard.css';

const MAIN_TOOLS = [
  { id: 'pen', label: 'Pen' },
  { id: 'highlight', label: 'Highlight' },
  { id: 'eraser', label: 'Eraser' },
  { id: 'text', label: 'Text' },
  { id: 'note', label: 'Note' },
  { id: 'move', label: 'Move notes' },
];

const SHAPE_TOOLS = [
  { id: 'line', label: 'Line' },
  { id: 'rect', label: 'Rectangle' },
  { id: 'ellipse', label: 'Ellipse' },
  { id: 'arrow', label: 'Arrow' },
];

const btn = {
  padding: '6px 10px',
  border: '1px solid #555',
  borderRadius: 4,
  background: '#333',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  whiteSpace: 'nowrap',
  flex: 'none',
};
const btnOn = { ...btn, background: '#7c3aed', borderColor: '#7c3aed' };
const btnOff = { ...btn, opacity: 0.45, cursor: 'not-allowed' };

const isPhone = () => typeof window !== 'undefined' && window.innerWidth < 640;

const Whiteboard = ({ isOpen, onClose }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [snap, setSnap] = useState(() => whiteboardService.snapshot());
  const [tick, setTick] = useState(0);
  const [tool, setTool] = useState('pen');
  const [showMore, setShowMore] = useState(false);
  const [color, setColor] = useState('#000000');
  const [size, setSize] = useState(3);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [dragOffset, setDragOffset] = useState(null);
  const [maximized, setMaximized] = useState(false);
  const [phone, setPhone] = useState(isPhone);
  const [textAt, setTextAt] = useState(null); // { x, y, mode } as fractions of the board
  const [textValue, setTextValue] = useState('');

  const canDraw = snap.canDraw;

  useEffect(() => {
    const onResize = () => setPhone(isPhone());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => whiteboardService.subscribe(setSnap), []);

  // Redraw whenever the board's on-screen size changes (resize handle, maximize, window size).
  useEffect(() => {
    const el = containerRef.current;
    if (!isOpen || !el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setTick((t) => t + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]);

  useEffect(() => {
    const cv = canvasRef.current;
    const el = containerRef.current;
    if (!isOpen || !cv || !el) return;
    const W = el.clientWidth;
    const H = el.clientHeight;
    cv.width = W;
    cv.height = H;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, W, H);
    drawStrokes(ctx, snap.strokes, snap.preview, W, H);
  }, [isOpen, snap, tick]);

  useEffect(() => {
    if (!dragOffset) return;
    const move = (e) => setPosition({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
    const up = () => setDragOffset(null);
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
  }, [dragOffset]);

  if (!isOpen) return null;

  const pointAt = (e) => {
    const r = containerRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const onPointerDown = (e) => {
    if (!canDraw) return;
    const p = pointAt(e);
    if (tool === 'move') {
      // Pick up the note under the pointer, if this user is allowed to move it.
      const board = containerRef.current;
      const note = whiteboardService.findNoteAt(p, board.clientWidth, board.clientHeight);
      if (note) {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        whiteboardService.beginNoteMove(note, p);
      }
      return;
    }
    if (tool === 'text' || tool === 'note') {
      setTextAt({ ...p, mode: tool });
      setTextValue('');
      return;
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
    whiteboardService.beginStroke(tool, color, size, p);
  };

  const onPointerMove = (e) => {
    const p = pointAt(e);
    whiteboardService.moveNote(p);
    whiteboardService.moveStroke(p);
  };
  const onPointerUp = () => {
    whiteboardService.endNoteMove();
    whiteboardService.endStroke();
  };

  const commitText = () => {
    if (textAt) {
      if (textAt.mode === 'note') whiteboardService.addNote(textAt.x, textAt.y, textValue, NOTE_COLOR);
      else whiteboardService.addText(textAt.x, textAt.y, textValue, color, size);
    }
    setTextAt(null);
    setTextValue('');
  };

  const cancelText = () => {
    setTextAt(null);
    setTextValue('');
  };

  const onHeaderDown = (e) => {
    if (phone || maximized || e.target.closest('.whiteboard-toolbar') || e.target.closest('button')) return;
    setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const savePng = () => {
    const cv = canvasRef.current;
    if (!cv) return;
    const out = document.createElement('canvas');
    out.width = cv.width;
    out.height = cv.height;
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, out.width, out.height);
    drawStrokes(ctx, snap.strokes, null, out.width, out.height);
    const a = document.createElement('a');
    a.href = out.toDataURL('image/png');
    a.download = `whiteboard-${Date.now()}.png`;
    a.click();
  };

  const el = containerRef.current;
  const noteSide = 120;
  let overlayX = 0;
  let overlayY = 0;
  if (textAt && el) {
    overlayX = textAt.x * el.clientWidth;
    overlayY = textAt.y * el.clientHeight;
    if (textAt.mode === 'note') {
      overlayX = Math.max(0, Math.min(overlayX, el.clientWidth - noteSide));
      overlayY = Math.max(0, Math.min(overlayY, el.clientHeight - noteSide - 40));
    }
  }

  const frame = phone
    ? {
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100%',
        height: 'auto',
        maxHeight: '100dvh',
        overflowY: 'auto',
        borderRadius: 0,
        zIndex: 1000,
      }
    : maximized
    ? {
        position: 'fixed',
        left: 8,
        top: 8,
        width: 'calc(100vw - 16px)',
        height: 'calc(100vh - 16px)',
        overflow: 'hidden',
        zIndex: 1000,
      }
    : {
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: 760,
        height: 480,
        minWidth: 360,
        minHeight: 300,
        maxWidth: '96vw',
        maxHeight: '90vh',
        resize: 'both',
        overflow: 'hidden',
        zIndex: 1000,
      };

  const boardBox = phone
    ? { position: 'relative', flex: 'none', width: '100%', aspectRatio: '4 / 3', overflow: 'hidden', background: '#FFFFFF' }
    : { position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden', background: '#FFFFFF' };

  const toolBtn = (t) => (canDraw ? (tool === t.id ? btnOn : btn) : btnOff);
  const Field = textAt && textAt.mode === 'note' ? 'textarea' : 'input';

  return (
    <div className="whiteboard-widget" style={{ ...frame, display: 'flex', flexDirection: 'column' }}>
      <div
        className="whiteboard-header"
        onMouseDown={onHeaderDown}
        style={{ flex: 'none', cursor: phone || maximized ? 'default' : 'grab', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <div className="whiteboard-title">
          <span>Whiteboard</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {!phone && (
            <button type="button" style={btn} onClick={() => setMaximized((m) => !m)}>
              {maximized ? 'Restore' : 'Maximize'}
            </button>
          )}
          <button className="whiteboard-close" onClick={onClose}>✕</button>
        </div>
      </div>

      <div className="whiteboard-toolbar" style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 8px' }}>
        <div
          style={{
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            overflowX: phone ? 'auto' : 'visible',
            flexWrap: phone ? 'nowrap' : 'wrap',
          }}
        >
          {MAIN_TOOLS.map((t) => (
            <button key={t.id} type="button" style={toolBtn(t)} disabled={!canDraw} onClick={() => setTool(t.id)}>
              {t.label}
            </button>
          ))}
          <button type="button" style={showMore ? btnOn : btn} onClick={() => setShowMore((v) => !v)} title="More tools">
            More ^
          </button>
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, flex: 'none' }}>
            Color
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} disabled={!canDraw} />
          </label>
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, flex: 'none' }}>
            Size
            <input
              type="range"
              min="1"
              max="20"
              value={size}
              onChange={(e) => setSize(parseInt(e.target.value, 10))}
              disabled={!canDraw}
            />
            {size}px
          </label>
          <button type="button" style={canDraw ? btn : btnOff} disabled={!canDraw} onClick={() => whiteboardService.undo()}>
            Undo
          </button>
          <button type="button" style={canDraw ? btn : btnOff} disabled={!canDraw} onClick={() => whiteboardService.redo()}>
            Redo
          </button>
          <button type="button" style={btn} onClick={savePng}>Save PNG</button>
          {snap.isHost && (
            <>
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, flex: 'none' }}>
                <input
                  type="checkbox"
                  checked={snap.allowAll}
                  onChange={(e) => whiteboardService.setAllowAll(e.target.checked)}
                />
                Let everyone draw
              </label>
              <button type="button" style={{ ...btn, background: '#dc2626' }} onClick={() => whiteboardService.clear()}>
                Clear
              </button>
            </>
          )}
          {!canDraw && <span style={{ fontSize: 12, color: '#ccc', flex: 'none' }}>View only. The host can allow drawing.</span>}
        </div>

        {showMore && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {SHAPE_TOOLS.map((t) => (
              <button key={t.id} type="button" style={toolBtn(t)} disabled={!canDraw} onClick={() => setTool(t.id)}>
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div ref={containerRef} style={boardBox}>
        <canvas
          ref={canvasRef}
          className="whiteboard-canvas"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'block',
            touchAction: 'none',
            cursor: canDraw ? (tool === 'move' ? 'move' : tool === 'text' || tool === 'note' ? 'text' : 'crosshair') : 'default',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        />
        {textAt && (
          <div
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: overlayX,
              top: overlayY,
              display: 'flex',
              flexDirection: textAt.mode === 'note' ? 'column' : 'row',
              gap: 4,
              zIndex: 5,
            }}
          >
            <Field
              autoFocus
              value={textValue}
              placeholder={textAt.mode === 'note' ? 'Type a note' : 'Type text'}
              onChange={(e) => setTextValue(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Escape') cancelText();
                // Notes are multi-line, so Enter adds a line and Ctrl+Enter adds the note.
                if (e.key === 'Enter' && (textAt.mode !== 'note' || e.ctrlKey || e.metaKey)) commitText();
              }}
              style={
                textAt.mode === 'note'
                  ? {
                      width: noteSide,
                      height: noteSide,
                      boxSizing: 'border-box',
                      fontSize: 12,
                      fontFamily: 'system-ui, sans-serif',
                      color: '#111827',
                      background: NOTE_COLOR,
                      border: '1px dashed #7c3aed',
                      outline: 'none',
                      padding: 8,
                      resize: 'none',
                    }
                  : {
                      fontSize: Math.max(12, size * 5),
                      color,
                      background: 'rgba(255,255,255,.95)',
                      border: '1px dashed #7c3aed',
                      outline: 'none',
                      padding: '4px 6px',
                      minWidth: 160,
                    }
              }
            />
            <div style={{ display: 'flex', gap: 4 }}>
              <button type="button" style={btn} onClick={commitText}>Add</button>
              <button type="button" style={btn} onClick={cancelText}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Whiteboard;
